import * as React from 'react'
import { Mail, Pencil, ShieldCheck, ShieldOff, Trash2, UserCheck, UserPlus, UserX } from 'lucide-react'
import {
  useCorrectVendorPersonEmail,
  useDeactivateVendorPerson,
  useDeletePendingVendorPerson,
  useInviteVendorPerson,
  useReactivateVendorPerson,
  useResendVendorPersonInvite,
  useSetVendorPersonOwner,
  useVendorPeople,
} from '@/hooks/useVendors'
import type { VendorPeopleScope } from '@/services/VendorService'
import { requestErrorMessage } from '@/helpers/apiClient'
import { correctVendorOwnerEmailInputSchema, inviteVendorUserInputSchema } from '@lib/input/vendor.input'
import type { VendorUserDto } from '@lib/dto/vendor-user.dto'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import { useToast } from '@/components/ui/toast'
import { UserStatusBadge } from '@/components/UserStatusBadge'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { possessive } from '@lib/helpers/format'

/** Someone to invite, filled in from the "wants to join" email's button (docs/public-site.spec.md). */
export interface InvitePrefill {
  name: string
  email: string
}

/** Invite someone: name, email, and whether they will own the vendor too. */
function InvitePersonForm({ scope, onDone, prefill }: { scope: VendorPeopleScope; onDone: () => void; prefill?: InvitePrefill | undefined }) {
  const [name, setName] = React.useState(prefill?.name ?? '')
  const [email, setEmail] = React.useState(prefill?.email ?? '')
  const [isOwner, setIsOwner] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const invite = useInviteVendorPerson(scope)

  function save(): void {
    setError(null)
    const parsed = inviteVendorUserInputSchema.safeParse({ name, email, isOwner })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the fields and try again.')
      return
    }
    invite.mutate(parsed.data, { onSuccess: onDone, onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <Card className="space-y-4 p-4">
      <Field label="Name" htmlFor="invite-name">
        <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoFocus />
      </Field>
      <Field label="Email" htmlFor="invite-email">
        <Input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
      </Field>
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          checked={isOwner}
          onChange={(e) => setIsOwner(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-line"
        />
        <span className="space-y-1">
          <span className="block font-medium text-ink">Make them an owner</span>
          <span className="block text-ink-muted">Owners invite and remove the company’s people.</span>
        </span>
      </label>
      {error ? <FormError>{error}</FormError> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button pending={invite.isPending} type="button" onClick={save} disabled={name.trim() === '' || email.trim() === ''}>
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </Button>
      </FormActions>
    </Card>
  )
}

/**
 * Put right a pending owner's mistyped email and invite them again — a super
 * admin's, for any company (decision 27). The first invitation stops working.
 */
function CorrectEmailDialog({
  vendorId,
  person,
  open,
  onOpenChange,
}: {
  vendorId: string
  person: VendorUserDto
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const correct = useCorrectVendorPersonEmail(vendorId)
  const [email, setEmail] = React.useState(person.email)
  const [error, setError] = React.useState<string | null>(null)

  function save(): void {
    const parsed = correctVendorOwnerEmailInputSchema.safeParse({ email })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a valid email.')
      return
    }
    setError(null)
    correct.mutate(
      { userId: person.id, email: parsed.data.email },
      { onSuccess: () => onOpenChange(false), onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Correct {possessive(person.name)} email</DialogTitle>
          <DialogDescription>They are invited again at this address. The invitation sent before stops working.</DialogDescription>
        </DialogHeader>
        <Field label="Email" htmlFor={`correct-email-${person.id}`}>
          <Input id={`correct-email-${person.id}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {error ? <FormError>{error}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button pending={correct.isPending} type="button" disabled={email.trim() === ''} onClick={save}>
            Save and invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** One person: who they are, whether they own the company, and what can be done to them. */
function PersonRow({
  scope,
  person,
  canManage,
  isMe,
}: {
  scope: VendorPeopleScope
  person: VendorUserDto
  canManage: boolean
  isMe: boolean
}) {
  const { toast } = useToast()
  const deactivate = useDeactivateVendorPerson(scope)
  const deletePending = useDeletePendingVendorPerson(scope)
  const reactivate = useReactivateVendorPerson(scope)
  const resend = useResendVendorPersonInvite(scope)
  const setOwner = useSetVendorPersonOwner(scope)
  const [resent, setResent] = React.useState(false)
  const [correcting, setCorrecting] = React.useState(false)

  // The API refuses what would leave the company without an owner, and says
  // what to do instead. That message is the one to show.
  const onError = (error: Error): void => toast(requestErrorMessage(error), { variant: 'error' })

  /**
   * The one action that ends an account: an unaccepted invitation is deleted,
   * since there is nothing to keep and holding the address would stop it being
   * re-sent to a corrected one; an account in use is deactivated, reversibly.
   * Nobody is offered deactivating themselves.
   */
  function lifecycleAction(): RowAction[] {
    if (person.status === 'DEACTIVATED') {
      return [
        {
          label: 'Reactivate',
          icon: UserCheck,
          onSelect: () => reactivate.mutate({ userId: person.id }, { onError }),
          disabled: reactivate.isPending,
        },
      ]
    }
    if (person.status === 'PENDING') {
      return [
        {
          label: 'Delete invitation',
          icon: Trash2,
          destructive: true,
          onSelect: () => deletePending.mutate({ userId: person.id }, { onError }),
          disabled: deletePending.isPending,
        },
      ]
    }
    if (isMe) return []
    return [
      {
        label: 'Deactivate',
        icon: UserX,
        destructive: true,
        onSelect: () => deactivate.mutate({ userId: person.id }, { onError }),
        disabled: deactivate.isPending,
      },
    ]
  }

  function ownerAction(): RowAction[] {
    if (person.status === 'DEACTIVATED') return []
    return [
      person.isOwner
        ? {
            label: isMe ? 'Step down as owner' : 'Remove as owner',
            icon: ShieldOff,
            onSelect: () => setOwner.mutate({ userId: person.id, isOwner: false }, { onError }),
            disabled: setOwner.isPending,
          }
        : {
            label: 'Make an owner',
            icon: ShieldCheck,
            onSelect: () => setOwner.mutate({ userId: person.id, isOwner: true }, { onError }),
            disabled: setOwner.isPending,
          },
    ]
  }

  const resendAction: RowAction[] =
    person.status === 'PENDING'
      ? [
          {
            label: resent ? 'Sent' : 'Resend invitation',
            icon: Mail,
            priority: true,
            onSelect: () => resend.mutate({ userId: person.id }, { onSuccess: () => setResent(true), onError }),
            disabled: resend.isPending || resent,
          },
        ]
      : []

  // A super admin's only: an owner's own company corrects nobody's address, and
  // a venue does it from Settings > Vendors for a company it set up.
  const correctAction: RowAction[] =
    scope.kind === 'vendor' && person.status === 'PENDING' && person.isOwner
      ? [{ label: 'Correct email', icon: Pencil, onSelect: () => setCorrecting(true) }]
      : []

  return (
    <TableRow>
      <TableCell>
        {scope.kind === 'vendor' && correcting ? (
          <CorrectEmailDialog vendorId={scope.vendorId} person={person} open={correcting} onOpenChange={setCorrecting} />
        ) : null}
        <p className="flex items-center gap-2 text-sm font-medium">
          {person.name}
          {isMe ? <span className="text-xs font-normal text-ink-muted">(you)</span> : null}
          {person.isOwner ? <Badge tone="info">Owner</Badge> : null}
        </p>
        <p className="text-xs text-ink-muted">{person.email}</p>
      </TableCell>
      <TableCell>
        <UserStatusBadge status={person.status} />
      </TableCell>
      {canManage ? (
        <TableActionsCell>
          <RowActions label={person.name} actions={[...resendAction, ...correctAction, ...ownerAction(), ...lifecycleAction()]} />
        </TableActionsCell>
      ) : null}
    </TableRow>
  )
}

/**
 * A vendor's people, and for whoever may change them, the controls to.
 *
 * A super admin manages any vendor's. The vendor's own people all see the list,
 * and its owners manage it; which one the viewer is comes from the list itself,
 * which marks each person an owner or not. The API holds the rules — a company
 * always keeps an owner, nobody deactivates themselves — and its refusals are
 * shown as they come.
 */
export function VendorPeople({
  scope,
  currentUserId,
  prefill,
}: {
  scope: VendorPeopleScope
  currentUserId?: string | null
  /** Open the invite form with these, for an owner arriving from a request to join. */
  prefill?: InvitePrefill | undefined
}) {
  const peopleQuery = useVendorPeople(scope)
  const [inviting, setInviting] = React.useState(prefill !== undefined)

  if (peopleQuery.isPending) {
    return <LoadingState label="Loading people…" />
  }
  if (peopleQuery.isError) {
    return <ErrorState message={requestErrorMessage(peopleQuery.error)} onRetry={() => void peopleQuery.refetch()} />
  }

  const people = peopleQuery.data.users
  const me = people.find((p) => p.id === currentUserId)
  const canManage = scope.kind === 'vendor' || me?.isOwner === true
  const columns = canManage ? 3 : 2

  return (
    <div className="space-y-4">
      {inviting && canManage ? <InvitePersonForm scope={scope} onDone={() => setInviting(false)} prefill={prefill} /> : null}
      <Table
        heading={`${String(people.length)} ${people.length === 1 ? 'person' : 'people'}`}
        actions={
          canManage && !inviting ? (
            <Button variant="ghost" size="sm" onClick={() => setInviting(true)}>
              <UserPlus className="h-4 w-4" /> Invite someone
            </Button>
          ) : undefined
        }
      >
        <TableHeader>
          <TableHead>Person</TableHead>
          <TableHead>Status</TableHead>
          {canManage ? <TableActionsHead /> : null}
        </TableHeader>
        <TableBody>
          {people.length === 0 ? (
            <TableEmptyRow colSpan={columns}>No one yet. Invite someone to manage this company.</TableEmptyRow>
          ) : null}
          {people.map((p) => (
            <PersonRow key={p.id} scope={scope} person={p} canManage={canManage} isMe={p.id === currentUserId} />
          ))}
        </TableBody>
      </Table>
      {!canManage ? (
        <p className="text-sm text-ink-muted">Only an owner can invite or remove people.</p>
      ) : null}
    </div>
  )
}
